import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { properties } from '../../../environments/properties';
import { B4UPLUS_ROUTES } from '../constants/b4uplus-routes';
import { AuthService } from './auth.service';
import { ProgramClassifier } from './program-classifier.service';
import { SubscriptionService } from './subscription.service';

/**
 * Shape resolved onto route.data for the ForU+ manage pages (plan, cancel-plan, update-payment).
 * `tokenObject` is the userinfo payload (carries SWY_SHOP_TOKEN etc.); `subscription` is the raw
 * getSubscription response (with its `.Data`). Components read both from route.data instead of
 * re-fetching.
 */
export interface ForUPlusManagePageData {
    tokenObject: any;
    subscription: any;
    /**
     * True only on the one-shot pass granted immediately after an immediate cancellation, where
     * the manage page renders display-only (message center, no cards) and then redirects to
     * /foruplus. Absent on every normal navigation.
     */
    justCancelled?: boolean;
}

/**
 * Data resolver for the ForU+ /foruplus/manage/{plan,cancel-plan,update-payment} pages.
 *
 * These pages used to run B4uplusRedirectGuard (which called getUserInfoCall + getSubscription to
 * classify the user) AND a TokenUserInfo resolver (getUserInfoCall again) AND a component-level
 * getSubscription — four network calls on a first navigation. getSubscription is data, not an auth
 * check, so this resolver owns the whole pre-render step: it fetches the token and subscription
 * once, applies the same gates the guard did (launch flag, session validity, active-ForU+ only),
 * and hands the data to the component via route.data. Redirects are performed imperatively followed
 * by `return EMPTY`, which cancels the in-flight navigation so the redirect wins.
 */
@Injectable({ providedIn: 'root' })
export class ForUPlusManageResolver implements Resolve<ForUPlusManagePageData> {

    constructor(
        private subscriptionService: SubscriptionService,
        private authService: AuthService,
        private programClassifier: ProgramClassifier,
        private router: Router
    ) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ForUPlusManagePageData> {
        // Flag OFF: the new /foruplus pages must never render — bounce to the legacy subscribe entry
        // (mirrors B4uplusRedirectGuard.flagOffDestination for the MANAGE group).
        if (!properties.featureFlags.enableB4UPlusLaunch) {
            this.router.navigate(['/freshpass/manage/subscribe']);
            return EMPTY;
        }

        return this.subscriptionService.getUserInfoCall().pipe(
            switchMap((userInfo: any) => {
                // Session gate: an expired/absent token goes to SSO before we call getSubscription
                // (this is the check the components used to do themselves).
                if (!this.authService.isTokenValidForSession(userInfo)) {
                    this.authService.redirectToSSO(userInfo);
                    return EMPTY;
                }

                const subUserInfo = { ...userInfo, authToken: userInfo.SWY_SHOP_TOKEN };
                const env = this.subscriptionService.getEnvironment();

                return this.subscriptionService.getUserSubscriptionDetails(subUserInfo, env).pipe(
                    switchMap((response: any): Observable<ForUPlusManagePageData> => {
                        const data = response?.Data;
                        // Resolved once: both the active-subscriber gate and the justCancelled
                        // exception below are anchored to the same server-returned classification.
                        const classification = this.programClassifier.classify(data);
                        const activeB4UPlus = classification === 'foruplus'
                            && !this.programClassifier.isCancelled(data);
                        if (!activeB4UPlus) {
                            // Special case, and the ONLY exception to the active-subscriber rule: a
                            // subscriber who has just cancelled is admitted once so the manage page
                            // can show the cancellation confirmation before redirecting them to
                            // /foruplus. The component renders display-only on this pass.
                            //
                            // Safe because it is gated on classify() as well as the navigation flag,
                            // so admission is anchored to server-returned data rather than to a
                            // client-supplied value: classify() reads planGeneration BEFORE any
                            // status check, so a cancelled B4U+ subscription still classifies as
                            // 'foruplus', while a never-enrolled user classifies as 'none' and is
                            // still bounced even if the flag were forged. Navigation state is also
                            // dropped on refresh, so the pass cannot be replayed.
                            const justCancelled = this.router.getCurrentNavigation()
                                ?.extras?.state?.['justCancelled'] === true;
                            if (justCancelled && classification === 'foruplus') {
                                return of({ tokenObject: userInfo, subscription: response, justCancelled: true });
                            }
                            // Only an active ForU+ subscriber may stay on the manage pages
                            // (mirrors the guard's MANAGE policy). Everyone else -> enrollment entry.
                            // Note: a 200 getSubscription whose subscription is CANCELLED is treated
                            // exactly like a not-enrolled ('none') user here — isCancelled() fails the
                            // activeB4UPlus check, so they are sent to /foruplus to (re)enroll.
                            this.router.navigate([B4UPLUS_ROUTES.LANDING]);
                            return EMPTY;
                        }
                        return of({ tokenObject: userInfo, subscription: response });
                    }),
                    // A lookup failure is treated like a non-member: send to the enrollment entry.
                    catchError(() => {
                        this.router.navigate([B4UPLUS_ROUTES.LANDING]);
                        return EMPTY;
                    })
                );
            }),
            // A getUserInfoCall failure (network, 401) is treated the same as a non-member.
            catchError(() => {
                this.router.navigate([B4UPLUS_ROUTES.LANDING]);
                return EMPTY;
            })
        );
    }
}
